import React, { useState, useEffect } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import { ProductsWrapper, ProductsListPanel }
  from '@fuww/library/src/Products';
import MoreProductsAvailable from './MoreProductsAvailable';
import ProductFilters from './ProductFilters';
import ProductsListWithQuery from './ProductsList';
import departmentAgeGroups, { DepartmentAgeGroupsProperties }
  from './departmentAgeGroups';
import departmentGenders, { DepartmentGendersProperties }
  from './departmentGenders';
import { loadFavorites, saveFavorites } from '../../lib/favorites';
import productsConnectionQuery
  from '../../lib/queries/products/productsConnectionQuery.graphql';
import { marketplaceProductListImageConfigurations }
  from '../../lib/imageConfigurations';
import { useSiteContext } from '../SiteContext';
import Loader from '../Loader';
import { saveRouterState } from '../../lib/savedRouterState';
import replaceRoute from '../../lib/replaceRoute';
import asArray from '../../lib/asArray';
import parseId from '../../lib/parseId';

const LIMIT = 16;

const productsPropertyTypes = {
  brandIdsScope: PropTypes.arrayOf(
    PropTypes.number,
  ),
  route: PropTypes.string,
  includeProfile: PropTypes.bool,
  marketId: PropTypes.number,
};

type ProductsProperties = InferProps<typeof productsPropertyTypes>;

const Products = ({
  brandIdsScope = [],
  route = 'product',
  includeProfile = false,
  marketId: marketIdFromProperties = null,
}: ProductsProperties) => {
  const { query, route: currentRouteWithSlash } = useRouter();
  const currentRoute = currentRouteWithSlash.slice(1);
  const marketId = marketIdFromProperties ?? parseId(query.marketId);
  const standId = parseId(query.standId);
  const {
    category,
    department,
    brandIds,
    sizes,
    seasons,
    colorIds,
    minPrice,
    maxPrice,
    printIds,
    materialIds,
    search,
    sort,
    favorites,
    model,
    sourceName,
    retailerName,
  } = query;
  const sourceId = query.sourceId && Number(query.sourceId);

  const { locale } = useSiteContext();
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  useEffect(() => {
    setFavoriteIds(loadFavorites());
  }, []);

  useEffect(() => {
    saveFavorites(favoriteIds);
    if (favoriteIds.size === 0 && favorites) {
      const { favorites: favoritesProperty, ...rest } = query;
      replaceRoute(
        currentRoute,
        rest,
      );
    }
  }, [favoriteIds, favorites, query, currentRoute]);

  const updateFavorites = (productId : number) => {
    if (favoriteIds.has(productId)) {
      favoriteIds.delete(productId);
    } else {
      favoriteIds.add(productId);
    }
    setFavoriteIds(new Set(favoriteIds));
  };

  const filters = {
    locales: [locale],
    ids: favorites ? [...favoriteIds] : null,
    mainCategories: asArray(category),
    genders: department ? departmentGenders[
      department as keyof DepartmentGendersProperties
    ] : [],
    ageGroups: department ? departmentAgeGroups[
      department as keyof DepartmentAgeGroupsProperties
    ] : [],
    sizes: typeof sizes === 'string' ? sizes.split(',') : [],
    seasons: typeof seasons === 'string' ? seasons.split(',') : [],
    brandIds: typeof brandIds === 'string'
      ? brandIds.split(',').map(Number)
      : brandIdsScope,
    colorIds: typeof colorIds === 'string'
      ? colorIds.split(',').map(Number)
      : [],
    priceAtLeast: minPrice ? Number(minPrice) : null,
    priceAtMost: maxPrice ? Number(maxPrice) : null,
    printIds: typeof printIds === 'string'
      ? printIds.split(',').map(Number)
      : [],
    materialIds: typeof materialIds === 'string'
      ? materialIds.split(',').map(Number)
      : [],
    search,
    sort,
    model: model && model === 'true',
  };

  const variables = {
    ...filters,
    marketId,
    standId,
    brandIdsScope,
    sourceId,
    sourceName,
    retailerName,
    locales: [locale],
    sort: sort ?? 'LATEST',
    imageConfigurations: marketplaceProductListImageConfigurations,
  };

  useEffect(() => {
    saveRouterState('products', { route: currentRoute, query });
  }, [currentRoute, query]);

  return (
    <ProductsWrapper>
      <ProductFilters
        variables={variables}
        favoriteIds={favoriteIds}
      />
      <ProductsListPanel>
        {typeof marketId === 'number' && (
          <MoreProductsAvailable
            variables={{ ...variables, marketId }}
          />
        )}
        <ProductsListWithQuery
          query={productsConnectionQuery}
          variables={{
            ...variables,
            first: LIMIT,
            includeProfile,
          }}
          errorMessage="Error loading products."
          marketId={marketId}
          standId={standId}
          favoriteIds={favoriteIds}
          updateFavorites={updateFavorites}
          loader={<Loader fullScreen />}
          route={route}
          includeProfile={includeProfile}
        />
      </ProductsListPanel>
    </ProductsWrapper>
  );
};

Products.propTypes = productsPropertyTypes;

export default Products;
